import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Card,
  CardBody,
  Icon,
  Flex,
  Image,
  Badge,
  useBreakpointValue,
  Stack,
  Divider,
  Spinner,
} from '@chakra-ui/react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  FaFistRaised,
  FaTrophy,
  FaUsers,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaPlay,
  FaMobile,
  FaQrcode,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { LandingHeader } from './LandingHeader';
import { useDataStore } from '../../contexts/DataStoreContext';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const navigate = useNavigate();

  return (
    <Box position="relative" minH="100vh" overflow="hidden">
      {/* Background with parallax effect */}
      <MotionBox
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgGradient="linear(to-br, dark.900, dark.800, brand.900)"
        style={{ y, opacity }}
      />

      {/* Overlay pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgImage="radial-gradient(circle at 20% 50%, rgba(239, 68, 68, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(239, 68, 68, 0.1) 0%, transparent 50%)"
      />

      <Container maxW="7xl" position="relative" zIndex={1}>
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="100vh"
          textAlign="center"
          py={20}
        >
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge
              colorScheme="brand"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
              mb={6}
            >
              PROFESSIONAL MMA EVENTS
            </Badge>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Heading
              as="h1"
              size="4xl"
              fontWeight="black"
              lineHeight="shorter"
              mb={6}
              bgGradient="linear(to-r, white, gray.300)"
              bgClip="text"
            >
              <Text as="span" color="brand.500">
                RCF
              </Text>
            </Heading>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Text
              fontSize="xl"
              color="gray.400"
              maxW="2xl"
              mx="auto"
              mb={8}
              lineHeight="tall"
            >
              Experience the ultimate in professional mixed martial arts.
              Vote on your favorite fighters and be part of the action with
              real-time predictions and live event engagement.
            </Text>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Stack
              direction={{ base: 'column', md: 'row' }}
              spacing={4}
              justify="center"
              mb={12}
            >
              <Button
                size="lg"
                colorScheme="brand"
                rightIcon={<FaFistRaised />}
                px={8}
                py={6}
                fontSize="lg"
                onClick={() => {
                  const element = document.querySelector('#events');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                _hover={{
                  transform: 'translateY(-3px)',
                  boxShadow: '0 20px 25px -5px rgba(239, 68, 68, 0.4)',
                }}
              >
                Start Voting
              </Button>
              <Button
                size="lg"
                variant="outline"
                rightIcon={<FaPlay />}
                px={8}
                py={6}
                fontSize="lg"
                borderColor="white"
                color="white"
                onClick={() => {
                  const element = document.querySelector('#events');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                _hover={{
                  bg: 'white',
                  color: 'dark.900',
                  transform: 'translateY(-3px)',
                }}
              >
                View Events
              </Button>
            </Stack>
          </motion.div>

        </Flex>
      </Container>

      {/* Scroll indicator */}
      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <Box textAlign="center" color="gray.400">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Text fontSize="sm" mb={2}>Scroll to explore</Text>
            <Box w="1px" h="8" bg="gray.400" mx="auto" />
          </motion.div>
        </Box>
      </motion.div>
    </Box>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: FaFistRaised,
      title: 'Elite Fighters',
      description: 'Watch world-class athletes compete at the highest level of mixed martial arts competition.',
    },
    {
      icon: FaTrophy,
      title: 'Championship Belts',
      description: 'Witness title fights and championship moments that define careers and create legends.',
    },
    {
      icon: FaUsers,
      title: 'Community',
      description: 'Join a passionate community of MMA fans and vote on your favorite fighters in real-time.',
    },
    {
      icon: FaCalendarAlt,
      title: 'Regular Events',
      description: 'Multiple events throughout the year featuring both rising stars and established veterans.',
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Prime Venues',
      description: 'Experience fights in state-of-the-art venues with premium seating and amenities.',
    },
    {
      icon: FaMobile,
      title: 'Mobile Voting',
      description: 'Vote on fights in real-time with our mobile-optimized platform and live updates.',
    },
  ];

  return (
    <Box py={20} bg="dark.800">
      <Container maxW="7xl">
        <VStack spacing={16}>
          <VStack spacing={4} textAlign="center">
            <Badge colorScheme="brand" fontSize="sm" px={4} py={2} borderRadius="full">
              WHY CHOOSE US
            </Badge>
            <Heading as="h2" size="2xl" fontWeight="bold">
              The Ultimate Fight Experience
            </Heading>
            <Text fontSize="lg" color="gray.400" maxW="2xl">
              From amateur prospects to championship contenders, RCF delivers
              unmatched entertainment with real-time voting and fan engagement.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} w="full">
            {features.map((feature, index) => (
              <MotionCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                bg="dark.700"
                borderColor="dark.600"
                _hover={{
                  borderColor: 'brand.500',
                  boxShadow: '0 10px 30px rgba(239, 68, 68, 0.2)',
                }}
              >
                <CardBody p={8}>
                  <VStack spacing={4} align="start">
                    <Box
                      p={3}
                      borderRadius="lg"
                      bg="brand.500"
                      color="white"
                    >
                      <Icon as={feature.icon} boxSize={6} />
                    </Box>
                    <Heading as="h3" size="md" color="white">
                      {feature.title}
                    </Heading>
                    <Text color="gray.400" lineHeight="tall">
                      {feature.description}
                    </Text>
                  </VStack>
                </CardBody>
              </MotionCard>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

const UpcomingEventsSection = () => {
  const { getAllEvents, getFightCountForEvent, loading } = useDataStore();
  const [events, setEvents] = useState(() => []);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading.events) {
      const eventsData = getAllEvents();
      // Filter for upcoming events and limit to 3
      const upcomingEvents = eventsData
        .filter(event => {
          if (!event?.date) return false;
          const eventDate = event.date instanceof Date ? event.date : new Date(event.date);
          const today = new Date();
          // Set both dates to midnight for date-only comparison
          const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
          const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          return eventDateOnly >= todayOnly;
        })
        .sort((a, b) => {
          const dateA = a.date instanceof Date ? a.date : new Date(a.date);
          const dateB = b.date instanceof Date ? b.date : new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 3);
      setEvents(upcomingEvents);
    }
  }, [getAllEvents, loading.events]);

  return (
    <Box id="events" py={20} bg="dark.900">
      <Container maxW="7xl">
        <VStack spacing={16}>
          <VStack spacing={4} textAlign="center">
            <Badge colorScheme="brand" fontSize="sm" px={4} py={2} borderRadius="full">
              UPCOMING EVENTS
            </Badge>
            <Heading as="h2" size="2xl" fontWeight="bold">
              Don't Miss These Fights
            </Heading>
            <Text fontSize="lg" color="gray.400" maxW="2xl">
              Vote on the most anticipated fights of the season.
              Join the action with real-time predictions and live engagement.
            </Text>
          </VStack>

          {loading.events ? (
            <VStack spacing={4} py={10}>
              <Spinner size="xl" color="brand.500" />
              <Text color="gray.400">Loading upcoming events...</Text>
            </VStack>
          ) : (events || []).length > 0 ? (
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} w="full">
              {(events || []).map((event, index) => (
                <MotionCard
                  key={event?.id || index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  whileHover={{ y: -10 }}
                  bg="dark.800"
                  borderColor="dark.700"
                  overflow="hidden"
                  cursor="pointer"
                  onClick={() => navigate(`/event/${event?.id}`)}
                  _hover={{
                    borderColor: 'brand.500',
                    boxShadow: '0 20px 40px rgba(239, 68, 68, 0.2)',
                  }}
                >
                  <Box position="relative">
                    {event?.coverImage ? (
                      <Image
                        src={event.coverImage}
                        alt={`${event.name} cover image`}
                        h="200px"
                        w="full"
                        objectFit="cover"
                      />
                    ) : (
                      <Box
                        h="200px"
                        w="full"
                        bg="dark.700"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon as={FaFistRaised} boxSize={12} color="brand.500" />
                      </Box>
                    )}
                    <Badge
                      position="absolute"
                      top={4}
                      left={4}
                      colorScheme="brand"
                      fontSize="xs"
                      px={2}
                      py={1}
                    >
                      {event?.status?.toUpperCase() || 'UPCOMING'}
                    </Badge>
                  </Box>
                  <CardBody p={6}>
                    <VStack spacing={4} align="start">
                      <VStack spacing={2} align="start" w="full">
                        <Heading as="h3" size="md" color="white">
                          {event?.name || 'RCF Event'}
                        </Heading>
                        <HStack spacing={4} fontSize="sm" color="gray.400">
                          <HStack>
                            <FaCalendarAlt />
                            <Text>
                              {event?.date ? (
                                event.date instanceof Date
                                  ? event.date.toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                  : new Date(event.date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                              ) : 'TBD'}
                            </Text>
                          </HStack>
                          <HStack>
                            <FaMapMarkerAlt />
                            <Text>{event?.location || 'TBD'}</Text>
                          </HStack>
                        </HStack>
                      </VStack>

                      <Text color="gray.300" fontWeight="medium">
                        {event?.description || `${getFightCountForEvent(event?.id) || 0} fights scheduled`}
                      </Text>

                      <Flex justify="space-between" align="center" w="full" pt={2}>
                        <Text color="brand.400" fontWeight="bold" fontSize="lg">
                          {getFightCountForEvent(event?.id) || 0} Fights
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="brand"
                          rightIcon={<FaFistRaised />}
                        >
                          Vote Now
                        </Button>
                      </Flex>
                    </VStack>
                  </CardBody>
                </MotionCard>
              ))}
            </SimpleGrid>
          ) : (
            <VStack spacing={4} py={10}>
              <Text color="gray.400" fontSize="lg">No upcoming events at the moment</Text>
              <Text color="gray.500" fontSize="sm">Check back soon for new fight announcements!</Text>
            </VStack>
          )}
        </VStack>
      </Container>
    </Box>
  );
};


const CTASection = () => {
  const navigate = useNavigate();

  return (
    <Box py={20} bg="dark.900">
      <Container maxW="4xl">
        <MotionBox
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card bg="dark.800" borderColor="brand.500" borderWidth={2}>
            <CardBody p={12}>
              <VStack spacing={8} textAlign="center">
                <VStack spacing={4}>
                  <Icon as={FaQrcode} boxSize={12} color="brand.500" />
                  <Heading as="h2" size="xl" color="white">
                    Quick Access with QR Code
                  </Heading>
                  <Text fontSize="lg" color="gray.400" maxW="2xl">
                    Scan QR codes at our events for instant access to voting,
                    live updates, exclusive content, and real-time fight statistics.
                  </Text>
                </VStack>

                <Divider borderColor="dark.600" />

                <VStack spacing={6}>
                  <Heading as="h3" size="lg" color="white">
                    Ready to Experience RCF?
                  </Heading>
                  <Text color="gray.400" fontSize="lg">
                    Join thousands of fans for an unforgettable night of world-class MMA action.
                  </Text>
                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    spacing={4}
                    w="full"
                    justify="center"
                  >
                    <Button
                      size="lg"
                      colorScheme="brand"
                      rightIcon={<FaFistRaised />}
                      px={8}
                      py={6}
                      fontSize="lg"
                      flex={{ base: 'none', sm: 1 }}
                      maxW="200px"
                      onClick={() => {
                        const element = document.querySelector('#events');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      Start Voting
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      borderColor="white"
                      color="white"
                      px={8}
                      py={6}
                      fontSize="lg"
                      flex={{ base: 'none', sm: 1 }}
                      maxW="200px"
                      onClick={() => {
                        const element = document.querySelector('#events');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      _hover={{
                        bg: 'white',
                        color: 'dark.900',
                      }}
                    >
                      View Events
                    </Button>
                  </Stack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </MotionBox>
      </Container>
    </Box>
  );
};

const FooterSection = () => {
  const navigate = useNavigate();

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <Box id="contact" py={16} bg="dark.800" borderTop="1px" borderColor="dark.700">
      <Container maxW="7xl">
        <VStack spacing={8}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
            w="full"
          >
            <VStack spacing={2} align={{ base: 'center', md: 'start' }}>
              <Heading as="h3" size="lg" color="white">
                RCF
              </Heading>
              <Text color="gray.400">
                Professional MMA Voting Platform
              </Text>
            </VStack>

            <HStack spacing={6} color="gray.400">
              <Text
                _hover={{ color: 'white', cursor: 'pointer' }}
                onClick={() => scrollToSection('#events')}
              >
                Events
              </Text>
              <Text
                _hover={{ color: 'white', cursor: 'pointer' }}
                onClick={() => navigate('/auth')}
              >
                Voting
              </Text>
              <Text
                _hover={{ color: 'white', cursor: 'pointer' }}
                onClick={() => scrollToSection('#contact')}
              >
                Contact
              </Text>
            </HStack>
          </Flex>

          <Divider borderColor="dark.600" />

          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
            w="full"
          >
            <Text color="gray.500" fontSize="sm">
              © 2024 RCF. All rights reserved.
            </Text>
            <HStack spacing={4} color="gray.500" fontSize="sm">
              <Text _hover={{ color: 'white', cursor: 'pointer' }}>Privacy Policy</Text>
              <Text _hover={{ color: 'white', cursor: 'pointer' }}>Terms of Service</Text>
            </HStack>
          </Flex>
        </VStack>
      </Container>
    </Box>
  );
};

export const LandingPage = () => {
  return (
    <Box>
      <LandingHeader />
      <HeroSection />
      <UpcomingEventsSection />
      <FeaturesSection />
      <CTASection />
      <FooterSection />
    </Box>
  );
};
